import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="blog-page" className="bg-[#FAF9F6]">
      <SEO
        title="Articles & Insights — Luxury Jewelry Magazine"
        description="In-depth articles on pre-owned luxury jewelry: authentication, market trends, designer spotlights, and expert buying guides from GIA-certified gemologists."
        url="/blog"
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#111]">Articles</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="overline overline-gold mb-6 animate-fade-in">The Jewelry Refined Magazine</p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] animate-fade-in-up">
                Insights, <span className="italic">Stories</span> &<br />
                Expert Guidance
              </h1>
            </div>
            <div className="lg:col-span-4 lg:border-l border-[#E5E0D8] lg:pl-8">
              <p className="text-[#555] leading-relaxed">
                Expert reporting on the pre-owned luxury jewelry market — from authentication
                deep-dives to designer waiting list confessionals.
              </p>
              <p className="overline mt-6">
                {posts.length} Article{posts.length === 1 ? '' : 's'} · Updated Weekly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article — Large Editorial Card */}
      {featured && (
        <section className="section-editorial border-b border-[#E5E0D8]">
          <div className="container-editorial">
            <div className="flex items-baseline justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
              <p className="overline overline-gold">Editor's Pick</p>
              <span className="text-xs text-[#888] tracking-wider">{formatDate(featured.published_at)}</span>
            </div>

            <Link
              to={`/blog/${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
              data-testid="featured-post"
            >
              <div className="lg:col-span-7 img-zoom aspect-[4/3] overflow-hidden">
                <img
                  src={featured.image_url}
                  alt={featured.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="lg:col-span-5">
                <p className="overline mb-4">{featured.category || 'Editorial'}</p>
                <h2 className="font-heading text-3xl lg:text-5xl font-light leading-[1.1] tracking-tight text-[#111] mb-6 group-hover:text-[#C69C6D] transition-colors duration-500">
                  {featured.title}
                </h2>
                <p className="text-[#555] text-lg leading-relaxed mb-8">{featured.excerpt}</p>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-px h-12 bg-[#C69C6D]" />
                  <div>
                    <p className="text-sm text-[#111]">{featured.author_name}</p>
                    <p className="text-xs text-[#888] mt-0.5">{featured.author_title}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1">
                  Read Story
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* All Articles — Magazine Grid */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Archive</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">All Articles</h2>
            </div>
            <p className="hidden md:block overline">{rest.length} Stories</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {rest.map((post, i) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group block"
                data-testid={`blog-card-${post.slug}`}
              >
                <div className="aspect-[4/5] overflow-hidden img-zoom mb-6">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="overline mb-3 flex items-center gap-3">
                  <span>{post.category || 'Editorial'}</span>
                  <span className="w-6 h-px bg-[#C69C6D]" />
                  <span className="text-[#888]">{formatDate(post.published_at)}</span>
                </p>
                <h3 className="font-heading text-2xl font-light leading-tight tracking-tight text-[#111] mb-3 group-hover:text-[#C69C6D] transition-colors duration-500">
                  {post.title}
                </h3>
                <p className="text-[#555] leading-relaxed line-clamp-2 mb-4">{post.excerpt}</p>
                <p className="text-sm text-[#111]">By {post.author_name}</p>
              </Link>
            ))}
          </div>

          {rest.length === 0 && featured && (
            <p className="text-center text-[#888] py-12">More stories arriving soon.</p>
          )}
        </div>
      </section>

      {/* Editorial Sign-off CTA */}
      <section className="bg-[#111] text-white py-20 lg:py-28">
        <div className="container-editorial text-center max-w-2xl mx-auto">
          <p className="overline text-[#C69C6D] mb-6">Stay Informed</p>
          <h2 className="heading-editorial text-3xl lg:text-5xl mb-6">
            The <span className="italic">Inner Circle</span> Newsletter
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mb-10">
            Authentication notes, market shifts, and our editors' weekly recommendations —
            delivered every Sunday.
          </p>
          <Link to="/contact" className="btn-editorial border-white text-white hover:bg-white hover:text-[#111]">
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
